import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Radium from 'radium';
import bind from 'bind-decorator';
import { Icon } from 'semantic-ui-react';
import { KEY_ENTER, KEY_ESC } from '../../constants';

// Inspiration for this component was taken from Megadraft.
// Demo: https://globocom.github.io/megadraft/#/?_k=0o17r3
// Code: https://github.com/globocom/megadraft/blob/08bce357573ea251b3a29308e12a7e34f2e868f4/src/components/Toolbar.js
//       https://github.com/globocom/megadraft/blob/master/src/entity_inputs/LinkInput.js

const s = {
  linkContainer: {
    position: 'absolute',
    top: '100%',
    width: '100%',
  },
  linkInput: {
    border: 'none',
    padding: '1.3rem 0',
    height: 'auto',
    width: '100%',
    fontSize: '1.4rem',
    outline: 'none',
    margin: 'auto',
  },
  inputPosition: {
    position: 'relative',
    maxWidth: '35rem',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 1rem',
    borderRadius: '4px',
    backgroundColor: 'white',
    border: '2px solid #eee',
    margin: '0 auto',
  },
  linkButton: {
    width: '30px',
    height: '100%',
    cursor: 'pointer',
  },
  arrow: {
    display: 'inline-block',
    top: '-14px',
    left: '50%',
    height: '0',
    width: '0',
    position: 'absolute',
    pointerEvents: 'none',
    borderWidth: '8px',
    borderStyle: 'solid',
    borderColor: 'transparent transparent #fff',
    WebkitTransition: 'border-color 0.2s ease-in-out',
    transition: 'border-color 0.2s ease-in-out',
    marginLeft: '-3rem',
  },
  errorMessage: {
    position: 'absolute',
    color: '$answer-incorrect-color',
    bottom: '5px',
    left: '1rem',
    fontSize: '0.8rem',
  },
};
class ComposerLinkInput extends Component {
  static displayName = 'ComposerLinkInput';

  static propTypes = {
    placeholder: PropTypes.string,
    hasLink: PropTypes.bool,
    addLinkEntity: PropTypes.func,
    removeLinkEntity: PropTypes.func,
    currentLink: PropTypes.string,
  };

  static defaultProps = {
    currentLink: '',
    placeholder: 'Type a link and press ENTER',
    hasLink: false,
    addLinkEntity: () => {},
    removeLinkEntity: () => {},

  };

  constructor(props) {
    super(props);
    this.state = {
      url: '',
      displayInput: true,
      errorMessage: '',
    };

    this.textInput = React.createRef();
  }

  componentDidMount() {
    this.textInput.current.focus();
  }

  get inputButton() {
    // If the link entity exists, show a button to delete it
    if (this.props.hasLink) {
      return (
        <span style={s.linkButton} onClick={this.props.removeLinkEntity}>
          <Icon name="chain broken" size="large" />
        </span>
      );
    // otherwise show an X button to cancel the input
    }
    return (
      <span style={s.linkButton} onClick={this.cancelEditing}>
        <Icon name="close" size="large" />
      </span>
    );
  }

  get errorMessage() {
    if (this.state.errorMessage) {
      return (
        <span style={s.errorMessage}>
          {this.state.errorMessage}
        </span>
      );
    } return null;
  }

  get currentUrl() {
    if (this.state.editingLink) {
      // If the user is in the middle of editing the url, display what they are typing
      return this.state.url;
    }
    // Otherwise display the link given through props.
    // If no link is given via props either, leave the field empty for input.
    return this.props.currentLink || '';
  }

  @bind
  onLinkKeyDown(event) {
    // When the user presses Enter, save the link
    if (event.keyCode === KEY_ENTER) {
      event.preventDefault();
      this.setLink(event);

    // If the user presses escape, cancel the editing and close the input field
    } else if (event.keyCode === KEY_ESC) {
      event.preventDefault();
      this.cancelEditing();
    }
  }

  @bind
  onLinkChange(event) {
    event.stopPropagation();
    const url = event.target.value;

    // If there was an error, when the link is erased, remove the error.
    if (url === '') {
      this.cancelError();
    }

    // Every time the user types into the field, update the state url
    this.setState({ url });
  }

  @bind
  setLink(event) {
    let { url } = this.state;
    // Add http:// to the url if it does not already have it
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `http://${url}`;
    }

    // https://gist.github.com/dperini/729294
    // Author: Diego Perini
    // License: MIT
    // eslint-disable-next-line max-len
    const expression = /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!10(?:\.\d{1,3}){3})(?!127(?:\.\d{1,3}){3})(?!169\.254(?:\.\d{1,3}){2})(?!192\.168(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\x{00a1}\-\x{ffff}0-9]+-?)*[a-z\x{00a1}\-\x{ffff}0-9]+)(?:\.(?:[a-z\x{00a1}\-\x{ffff}0-9]+-?)*[a-z\x{00a1}\-\x{ffff}0-9]+)*(?:\.(?:[a-z\x{00a1}\-\x{ffff}]{2,})))(?::\d{2,5})?(?:\/[^\s]*)?$/ig;

    const regex = new RegExp(expression);

    if (!url.match(regex)) {
      this.setError('Please enter a valid link.');
      this.textInput.focus();
      return;
    }
    this.cancelError();

    this.props.addLinkEntity(url);

    // Force blur to work around Firefox's NS_ERROR_FAILURE
    event.target.blur();
  }

  /**
   * Canceling hides the input field, and clears out the state
   */
  @bind
  cancelEditing() {
    this.setState({
      url: '',
      displayInput: false,
      editingLink: false,
      errorMessage: '',
    });
  }

  /**
   * Displays an error message below the field
   * @param {string} errorMessage
   */
  @bind
  setError(errorMessage) {
    this.setState({
      errorMessage: errorMessage || 'Failed to set link.',
    });
  }

  /**
   * Removes the error message
   */
  @bind
  cancelError() {
    this.setState({
      errorMessage: '',
    });
  }

  @bind
  handleOnFocus() {
    // When the input field is focused on, update the state url to refect the latest info
    // This way it can be edited directly. Also keep track that the link is being edited.
    const currentUrl = this.state.url || this.props.currentLink || '';
    this.setState({
      editingLink: true,
      url: currentUrl,
    });
  }

  @bind
  handleOnBlur() {
    // When the input field is not selected, keep track that it is not being edited
    this.setState({
      editingLink: false,
    });
  }

  render() {
    if (this.state.displayInput) {
      return (
        <div style={s.linkContainer}>
          <div style={s.inputPosition}>
            <input
              style={s.linkInput}
              ref={this.textInput}
              onFocus={this.handleOnFocus}
              onBlur={this.handleOnBlur}
              placeholder={this.props.placeholder}
              onKeyDown={this.onLinkKeyDown}
              onChange={this.onLinkChange}
              value={this.currentUrl}
            />
            {this.inputButton}
            {this.errorMessage}
          </div>
          <span style={s.arrow} />
        </div>
      );
    } return null;
  }
}

export default Radium(ComposerLinkInput);
